import { ref, computed } from 'vue';
import { t } from '@/utils/i18n-helper';
import { isPlazoPagable } from '@/composables/front/perfil/plazos';

/**
 * Composable genérico de selección y pago de plazos.
 * Extraído de useCantera para ser reutilizado por useAbonos.
 *
 * @param {Object} options
 * @param {import('vue').Ref<Array>} options.items
 * @param {import('vue').Ref<Map<number|string, Object>>} options.detalleCache
 * @param {(plazoIds: Array<number|string>, extra?: Object) => Object} options.buildPaymentPayload
 * @param {(itemId: number|string) => Promise<void>} [options.onRefreshDetail]
 * @param {(domainPayload: Object) => Promise<void>} options.processPayment
 */
export function usePlazosSelection({ items, detalleCache, buildPaymentPayload, onRefreshDetail, processPayment }) {
    /** @type {import('vue').Ref<Map<number|string, Set<number|string>>>} */
    const selectedPlazos = ref(new Map());

    /**
     * Alterna la selección de un plazo pagable de un item.
     * No hace nada si el estado no es pagable (0 o 4).
     * @param {number|string} itemId
     * @param {number|string} plazoId
     * @param {number} estadoPlazo
     */
    const togglePlazo = (itemId, plazoId, estadoPlazo) => {
        if (!isPlazoPagable(estadoPlazo)) return;

        const nextMap = new Map(selectedPlazos.value);
        const set = new Set(nextMap.get(itemId) ?? []);

        if (set.has(plazoId)) {
            set.delete(plazoId);
        } else {
            set.add(plazoId);
        }

        nextMap.set(itemId, set);
        selectedPlazos.value = nextMap;
    };

    /**
     * Comprueba si un plazo de un item está seleccionado.
     * @param {number|string} itemId
     * @param {number|string} plazoId
     * @returns {boolean}
     */
    const isSelected = (itemId, plazoId) => {
        return selectedPlazos.value.get(itemId)?.has(plazoId) ?? false;
    };

    /** Número total de plazos seleccionados en todos los items. */
    const selectedCount = computed(() => {
        let count = 0;
        for (const set of selectedPlazos.value.values()) {
            count += set.size;
        }
        return count;
    });

    /**
     * Suma de importes de los plazos seleccionados.
     * Requiere que el detalle esté cargado en caché.
     */
    const totalSeleccionado = computed(() => {
        let total = 0;
        for (const [itemId, plazoIds] of selectedPlazos.value.entries()) {
            const detalle = detalleCache.value.get(itemId);
            if (!detalle?.plazos) continue;
            for (const plazo of detalle.plazos) {
                if (plazoIds.has(plazo.id)) {
                    total += plazo.importe_con_gd ?? plazo.importe ?? 0;
                }
            }
        }
        return total;
    });

    /**
     * Limpia la selección de un item concreto o de todos.
     * @param {number|string} [itemId=null]
     */
    const clearSelection = (itemId = null) => {
        if (itemId === null) {
            selectedPlazos.value = new Map();
        } else {
            const nextMap = new Map(selectedPlazos.value);
            nextMap.delete(itemId);
            selectedPlazos.value = nextMap;
        }
    };

    // -------------------------------------------------------------------------
    // Pago de plazos
    // -------------------------------------------------------------------------
    const pagoLoading = ref(false);
    const pagoError = ref(null);

    /**
     * Envía el pago de plazos seleccionados al endpoint legacy.
     * Delega la construcción de la petición HTTP a canteraService.
     * @param {Array<number|string>} plazoIds — IDs de plazos a pagar
     * @returns {Promise<void>}
     */
    const pagarPlazos = async(plazoIds, extra = {}) => {
        pagoLoading.value = true;
        pagoError.value = null;

        try {
            const domainPayload = buildPaymentPayload(plazoIds, extra);
            await processPayment(domainPayload);
        } catch (err) {
            pagoError.value = err?.response?.data?.message
                ?? err?.message
                ?? t('perfil.error_pago_plazos', {}, 'Error al procesar el pago');

            if (process.env.NODE_ENV !== 'production') {
                console.error('[usePlazosSelection] Error al pagar plazos:', err);
            }
            throw err;
        } finally {
            pagoLoading.value = false;
        }
    };

    /**
     * Localiza qué items contienen los plazos enviados.
     * El detalle ya está cargado en memoria.
     * @param {Array<number|string>} plazoIds
     * @returns {Array<number|string>}
     */
    const resolveItemsFromPlazos = plazoIds => {
        const affectedItems = [];

        for (const item of items.value) {
            const detalle = detalleCache.value.get(item.id);
            if (!detalle?.plazos) continue;

            if (detalle.plazos.some(plazo => plazoIds.includes(plazo.id))) {
                affectedItems.push(item.id);
            }
        }

        return affectedItems;
    };

    /**
     * Recarga el detalle de un item si se proporcionó onRefreshDetail
     * y limpia la selección tras el refresco.
     *
     * La invalidación de caché es responsabilidad del callback
     * onRefreshDetail (propiedad de quien posee la caché).
     *
     * @param {number|string} itemId
     */
    const refreshDetalle = async itemId => {
        if (onRefreshDetail) {
            await onRefreshDetail(itemId);
        }

        clearSelection(itemId);
    };

    /**
     * Ejecuta el pago y refresca sólo los detalles afectados.
     * @param {Array<number|string>} plazoIds
     */
    const pagarYRefrescarPlazos = async(plazoIds, extra = {}) => {
        await pagarPlazos(plazoIds, extra);

        clearSelection();

        const affectedItems = resolveItemsFromPlazos(plazoIds);

        for (const itemId of affectedItems) {
            await refreshDetalle(itemId);
        }
    };

    return {
        selectedPlazos,
        isPagable: isPlazoPagable,
        togglePlazo,
        isSelected,
        selectedCount,
        totalSeleccionado,
        clearSelection,
        pagoLoading,
        pagoError,
        pagarPlazos,
        pagarYRefrescarPlazos,
        refreshDetalle,
    };
}
